/* ============================================================================
   PRESTIGE THEME - PRODUCT BUNDLE OFFER WEB COMPONENT
   ============================================================================ */

if (!window.customElements.get('product-bundle-offer')) {
  class ProductBundleOffer extends HTMLElement {
    constructor() {
      super();
      this.selectedCard = null;
      this.selectedOfferIndex = 1;
      this.activeSlotElement = null;
      this.eligibleProducts = [];
      this.mainProductData = null;
    }

    connectedCallback() {
      this.loadEligibleData();
      this.loadMainProductData();
      this.initCards();
      this.initMainProductSizeSync();
      this.initChooseModal();
      this.initSlotActions();
      this.initAddToCart();
    }

    loadEligibleData() {
      const dataScript = this.querySelector('.product-bundle-eligible-json');
      if (dataScript) {
        try {
          this.eligibleProducts = JSON.parse(dataScript.textContent || '[]');
        } catch (e) {
          console.error('Bundle: eligible products parse error', e);
        }
      }
    }

    loadMainProductData() {
      const dataScript = this.querySelector('.product-main-data-json');
      if (dataScript) {
        try {
          this.mainProductData = JSON.parse(dataScript.textContent || '{}');
        } catch (e) {
          console.error('Bundle: main product data parse error', e);
        }
      }
    }

    /* ------------------------------------------------------------------------
       CARDS
       ------------------------------------------------------------------------ */
    initCards() {
      const cards = this.querySelectorAll('.product-bundle-card');
      if (!cards.length) return;

      cards.forEach(card => {
        card.addEventListener('click', (e) => {
          if (
            e.target.closest('.product-bundle-slots') ||
            e.target.closest('.product-bundle-add-trigger-row') ||
            e.target.tagName === 'BUTTON' ||
            e.target.tagName === 'SELECT'
          ) return;
          this.selectCard(card);
        });
      });

      const defaultCard =
        this.querySelector('.product-bundle-card[data-selected="true"]') || cards[0];
      if (defaultCard) this.selectCard(defaultCard);
    }

    selectCard(card) {
      if (this.selectedCard === card) return;

      this.querySelectorAll('.product-bundle-card').forEach(c => {
        c.classList.remove('product-bundle-card--selected');
        c.setAttribute('data-selected', 'false');
        const radio = c.querySelector('.product-bundle-card__radio');
        if (radio) radio.checked = false;
        const slotsArea = c.querySelector('.product-bundle-slots');
        if (slotsArea) slotsArea.hidden = true;
      });

      card.classList.add('product-bundle-card--selected');
      card.setAttribute('data-selected', 'true');
      const radio = card.querySelector('.product-bundle-card__radio');
      if (radio) radio.checked = true;
      const slotsArea = card.querySelector('.product-bundle-slots');
      if (slotsArea) slotsArea.hidden = false;

      this.selectedCard = card;
      this.selectedOfferIndex = card.dataset.offerIndex;
      this.hideError();
      this.updateCtaPrice();
    }

    /* ------------------------------------------------------------------------
       MAIN PRODUCT SIZE SYNC  (SLOT #1 <-> NATIVE PRESTIGE VARIANT PICKER)
       ------------------------------------------------------------------------ */
    initMainProductSizeSync() {
      const mainSelects = this.querySelectorAll('.slot-main-size-select');
      const mainColorSelects = this.querySelectorAll('.slot-main-color-select');

      // Bundle color dropdown in SLOT #1 -> update self-contained + try native picker
      mainColorSelects.forEach(select => {
        select.addEventListener('change', (e) => {
          const selectedColor = e.target.value;
          if (!selectedColor) return;

          mainColorSelects.forEach(s => {
            if (s !== e.target) s.value = selectedColor;
          });

          this._updateMainProductState({ color: selectedColor });
          this._pushColorToNativePicker(selectedColor);
        });
      });

      // Bundle size dropdown in SLOT #1 -> update self-contained + try native picker
      mainSelects.forEach(select => {
        select.addEventListener('change', (e) => {
          const opt = e.target.options[e.target.selectedIndex];
          if (!opt) return;

          const selectedVariantId = e.target.value;
          const selectedSizeTitle = (opt.dataset.sizeTitle || opt.text.replace(' - Készlethiány', '')).trim();

          mainSelects.forEach(s => {
            if (s !== e.target) s.value = selectedVariantId;
          });

          this._updateMainProductState({ size: selectedSizeTitle, variantId: selectedVariantId });
          this._pushSizeToNativePicker(selectedSizeTitle);
        });
      });

      // Native picker variant:change -> bundle (if native picker is present & used)
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this._onNativeVariantChange(e.detail.variant);
        }
      });
    }

    /**
     * Self-contained internal state update for Main Product (SLOT #1).
     * Works 100% whether native <variant-picker> is present, hidden, or deleted!
     */
    _updateMainProductState({ color, size, variantId }) {
      const d = this.mainProductData;
      if (!d || !Array.isArray(d.variants) || !d.variants.length) return;

      const colorSelect = this.querySelector('.slot-main-color-select');
      const sizeSelect = this.querySelector('.slot-main-size-select');

      const currentColor = color !== undefined
        ? color
        : (colorSelect ? colorSelect.value : (this.querySelector('.slot-main-color-val')?.textContent.trim() || ''));

      let currentSize = size;
      if (!currentSize && sizeSelect && sizeSelect.selectedIndex >= 0) {
        const opt = sizeSelect.options[sizeSelect.selectedIndex];
        if (opt) currentSize = (opt.dataset.sizeTitle || opt.text.replace(' - Készlethiány', '')).trim();
      }

      // Find best variant match
      let matchedVariant = null;
      if (variantId) {
        matchedVariant = d.variants.find(v => String(v.id) === String(variantId));
      }
      if (!matchedVariant && currentColor && currentSize) {
        matchedVariant =
          d.variants.find(v => v.color === currentColor && String(v.size) === String(currentSize) && v.available) ||
          d.variants.find(v => v.color === currentColor && String(v.size) === String(currentSize));
      }
      if (!matchedVariant && currentColor) {
        matchedVariant =
          d.variants.find(v => v.color === currentColor && v.available) ||
          d.variants.find(v => v.color === currentColor);
      }
      if (!matchedVariant) {
        matchedVariant = d.variants[0];
      }

      if (!matchedVariant) return;

      const finalVariantId = String(matchedVariant.id);
      const finalColor     = matchedVariant.color || currentColor;
      const finalSize      = matchedVariant.size  || currentSize;

      // 1. Update component data attributes
      this.setAttribute('data-current-variant-id', finalVariantId);
      this.setAttribute('data-current-variant-size', finalSize);

      // 2. Update hidden form input[name="id"] for cart API
      const mainForm = document.querySelector('form[action*="/cart/add"]');
      if (mainForm) {
        const idInput = mainForm.querySelector('input[name="id"], select[name="id"]');
        if (idInput && idInput.value !== finalVariantId) {
          idInput.value = finalVariantId;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // 3. Update Color dropdown value across all cards
      this.querySelectorAll('.slot-main-color-select').forEach(sel => {
        if (sel.value !== finalColor) sel.value = finalColor;
      });

      // 4. Re-render Size dropdown options filtered to finalColor across all cards
      const hasColors = !!d.has_multiple_colors;
      this.querySelectorAll('.slot-main-size-select').forEach(sel => {
        if (hasColors && finalColor) {
          const colorVars = d.variants.filter(v => v.color === finalColor);
          if (colorVars.length) {
            sel.innerHTML = colorVars.map(v => {
              const selected = String(v.id) === finalVariantId ? 'selected="selected"' : '';
              const disabled = !v.available ? 'disabled="disabled"' : '';
              const statusText = !v.available ? ' - Készlethiány' : '';
              return '<option value="' + v.id + '" data-size-title="' + this._esc(v.size) + '" data-color-title="' + this._esc(v.color) + '" ' + selected + ' ' + disabled + '>' + this._esc(v.size) + statusText + '</option>';
            }).join('');
          }
        }
        sel.value = finalVariantId;
      });

      // 5. Update SLOT #1 Thumbnail Image across all cards
      const imgSrc = this._resolveVariantImage({ id: finalVariantId }, matchedVariant, finalColor);
      if (imgSrc) {
        this.querySelectorAll('.slot-main-img').forEach(img => {
          img.src = imgSrc;
        });
        this._updateMainGalleryImage(imgSrc, matchedVariant);
      }

      // 6. Sync Prestige Inventory block and Product section rerender
      this._syncProductInventoryAndRerender(matchedVariant, finalVariantId);
    }

    /**
     * Synchronizes Prestige's native Inventory block, Price, SKU, Badges, Buy Buttons
     * even when Prestige's native <variant-picker> is hidden or completely removed.
     */
    async _syncProductInventoryAndRerender(matchedVariant, finalVariantId) {
      if (!finalVariantId) return;

      const mainForm = document.querySelector('form[action*="/cart/add"]') || (this.mainProductData?.product_form_id ? document.forms[this.mainProductData.product_form_id] : null);
      const inventoryBlock = document.querySelector('.product-info__block-item[data-block-type="inventory"], variant-inventory, .inventory');

      // 1. Immediate optimistic UI update for inventory
      if (inventoryBlock && matchedVariant) {
        const isAvailable = !!matchedVariant.available;
        const invQty = Number(matchedVariant.inventory_quantity) || 0;
        const invSpan = inventoryBlock.querySelector('span');

        if (invSpan) {
          if (!isAvailable) {
            invSpan.textContent = 'Elfogyott';
            inventoryBlock.className = inventoryBlock.className.replace(/\btext-(success|warning|error)\b/g, '') + ' text-error';
          } else if (invQty > 0 && invQty <= 5) {
            invSpan.textContent = `Csak ${invQty} db maradt raktáron`;
            inventoryBlock.className = inventoryBlock.className.replace(/\btext-(success|warning|error)\b/g, '') + ' text-warning';
          } else {
            invSpan.textContent = 'Raktáron';
            inventoryBlock.className = inventoryBlock.className.replace(/\btext-(success|warning|error)\b/g, '') + ' text-success';
          }
        }
      }

      // 2. Fetch fresh Shopify section HTML with the new variant id
      try {
        const sectionId = this.mainProductData?.section_id || document.querySelector('[data-section-id]')?.dataset.sectionId || 'main-product';
        const rootUrl = window.Shopify?.routes?.root || '/';
        const handle = this.mainProductData?.handle || (window.location.pathname.split('/products/')[1] ? window.location.pathname.split('/products/')[1].split('?')[0] : '');
        
        let fetchUrl = window.location.pathname;
        if (handle) {
          fetchUrl = `${rootUrl}products/${handle}`;
        }

        const params = new URLSearchParams();
        params.set('variant', finalVariantId);
        params.set('section_id', sectionId);

        const response = await fetch(`${fetchUrl}?${params.toString()}`);
        if (response.ok) {
          const responseText = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(responseText, 'text/html');

          // Replace inventory block directly
          const newInventoryBlock = doc.querySelector('.product-info__block-item[data-block-type="inventory"], variant-inventory');
          const currentInventoryBlock = document.querySelector('.product-info__block-item[data-block-type="inventory"], variant-inventory');
          if (currentInventoryBlock && newInventoryBlock) {
            currentInventoryBlock.replaceWith(newInventoryBlock);
          }

          // Replace price block if present
          const newPriceBlock = doc.querySelector('.product-info__block-item[data-block-type="price"]');
          const currentPriceBlock = document.querySelector('.product-info__block-item[data-block-type="price"]');
          if (currentPriceBlock && newPriceBlock) {
            currentPriceBlock.replaceWith(newPriceBlock);
          }

          // Replace other blocks (SKU, badges, buy-buttons, etc.)
          const blockTypes = ['sku', 'badges', 'payment-terms', 'buy-buttons', 'pickup-availability'];
          blockTypes.forEach((bType) => {
            const newBlock = doc.querySelector(`.product-info__block-item[data-block-type="${bType}"]`);
            const currentBlock = document.querySelector(`.product-info__block-item[data-block-type="${bType}"]`);
            if (currentBlock && newBlock) {
              if (bType === 'buy-buttons') {
                const curBuy = currentBlock.querySelector('buy-buttons');
                const newBuy = newBlock.querySelector('buy-buttons');
                if (curBuy && newBuy) curBuy.replaceWith(newBuy);
              } else {
                currentBlock.replaceWith(newBlock);
              }
            }
          });

          // Dispatch standard Prestige events to product form
          if (mainForm) {
            const fragment = document.createRange().createContextualFragment(responseText);
            mainForm.dispatchEvent(new CustomEvent('product:rerender', {
              bubbles: true,
              detail: {
                htmlFragment: fragment,
                productChange: false
              }
            }));

            mainForm.dispatchEvent(new CustomEvent('variant:change', {
              bubbles: true,
              detail: {
                formId: mainForm.id,
                variant: matchedVariant
              }
            }));
          }

          // Re-init dynamic checkout button if available
          if (window.Shopify && window.Shopify.PaymentButton) {
            window.Shopify.PaymentButton.init();
          }
        }
      } catch (err) {
        console.warn('[Bundle System] Could not fetch section rerender for inventory:', err);
      }
    }

    /**
     * Get high-resolution URL from a Shopify CDN image URL (replaces _240x or width=240 with _1200x or width=1200).
     */
    _getHighResCdnUrl(src) {
      if (!src || typeof src !== 'string') return src;
      let highRes = src.replace(/_\d+x(\.\w{2,5})(\?|$)/, '_1200x$1$2');
      highRes = highRes.replace(/([?&]width=)\d+/, '$11200');
      return highRes;
    }

    /**
     * Update main product gallery image on page if <product-gallery> is present.
     * Uses 1200px high-res URL to avoid pixelation, or selects matching gallery slide.
     */
    _updateMainGalleryImage(imgSrc, matchedVariant) {
      if (!imgSrc) return;
      const gallery = document.querySelector('product-gallery');
      if (gallery) {
        if (matchedVariant) {
          const mediaId = String(matchedVariant.featured_media?.id || matchedVariant.featured_image?.id || '');
          if (mediaId) {
            const matchingMedia = gallery.querySelector('[data-media-id="' + mediaId + '"]');
            if (matchingMedia) {
              const carousel = gallery.querySelector('.product-gallery__carousel');
              if (carousel && typeof carousel.select === 'function') {
                const allMedia = Array.from(gallery.querySelectorAll('.product-gallery__media'));
                const targetIdx = allMedia.indexOf(matchingMedia);
                if (targetIdx >= 0) {
                  carousel.select(targetIdx, { instant: true });
                  return;
                }
              }
              matchingMedia.scrollIntoView({ block: 'start', behavior: 'smooth' });
              return;
            }
          }
        }

        const highResUrl = this._getHighResCdnUrl(imgSrc);
        const primaryImg = gallery.querySelector('.product-gallery__media:not([hidden]) img, .product-gallery .is-initial img, .product-gallery img');
        if (primaryImg && primaryImg.src !== highResUrl) {
          primaryImg.src = highResUrl;
          if (primaryImg.srcset) primaryImg.srcset = '';
        }
      }
    }

    /**
     * Called when Prestige fires variant:change on the product form.
     * Updates: color label & select, size dropdown (filtered to new color), thumbnail image.
     */
    _onNativeVariantChange(variant) {
      if (!variant || !variant.id) return;

      const d = this.mainProductData;
      const variantId = String(variant.id);

      this.setAttribute('data-current-variant-id', variantId);

      // Look up variant in our local JSON data
      const vData = (d && Array.isArray(d.variants))
        ? (d.variants.find(v => String(v.id) === variantId) || null)
        : null;

      // Resolve color and size values
      const colorIdx = (d && typeof d.color_option_index === 'number' && d.color_option_index >= 0)
        ? d.color_option_index : -1;
      const sizeIdx  = (d && typeof d.size_option_index  === 'number' && d.size_option_index  >= 0)
        ? d.size_option_index  : -1;

      const colorVal = (vData && vData.color)
        || (colorIdx >= 0 && Array.isArray(variant.options) ? variant.options[colorIdx] : '')
        || '';

      const sizeVal = (vData && vData.size)
        || (sizeIdx >= 0 && Array.isArray(variant.options) ? variant.options[sizeIdx] : variant.title)
        || variant.title;

      this.setAttribute('data-current-variant-size', sizeVal);

      const hasColors = !!(d && d.has_multiple_colors);

      // 1. Update color label & select
      if (hasColors && colorVal) {
        this.querySelectorAll('.slot-main-color-val').forEach(el => {
          el.textContent = colorVal;
        });
        this.querySelectorAll('.slot-main-color-select').forEach(select => {
          select.value = colorVal;
        });
      }

      // 2. Re-render size dropdown filtered to the new color
      this.querySelectorAll('.slot-main-size-select').forEach(select => {
        if (hasColors && colorVal && d && Array.isArray(d.variants)) {
          const colorVars = d.variants.filter(v => v.color === colorVal);
          if (colorVars.length) {
            select.innerHTML = colorVars.map(v => {
              const sel = String(v.id) === variantId ? 'selected="selected"' : '';
              const dis = !v.available ? 'disabled="disabled"' : '';
              const txt = !v.available ? ' - Készlethiány' : '';
              return '<option value="' + v.id + '" data-size-title="' + this._esc(v.size) + '" data-color-title="' + this._esc(v.color) + '" ' + sel + ' ' + dis + '>' + this._esc(v.size) + txt + '</option>';
            }).join('');
          }
        } else {
          // Single-color: just sync the selected value
          select.value = variantId;
        }
      });

      // 3. Update thumbnail image
      const imgSrc = this._resolveVariantImage(variant, vData, colorVal);
      if (imgSrc) {
        this.querySelectorAll('.slot-main-img').forEach(img => {
          img.src = imgSrc;
        });
      }
    }

    /**
     * Resolve best image URL for the given variant. 5 fallback levels.
     */
    _resolveVariantImage(variant, vData, colorVal) {
      const d = this.mainProductData;

      // 1. featured_media or featured_image from the native event payload
      if (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
        return this._resizeCdnUrl(variant.featured_media.preview_image.src, 240);
      }
      if (variant.featured_image) {
        if (typeof variant.featured_image === 'object' && variant.featured_image.src) {
          return this._resizeCdnUrl(variant.featured_image.src, 240);
        }
        if (typeof variant.featured_image === 'string' && variant.featured_image) {
          return variant.featured_image;
        }
      }

      // 2. Pre-generated URL from our Liquid JSON (image_url: width: 240)
      if (vData && vData.featured_image) return vData.featured_image;

      // 3. color_images map: first variant's image for this color
      if (colorVal && d && d.color_images && d.color_images[colorVal]) {
        return d.color_images[colorVal];
      }

      // 4. Active visible image from main product gallery DOM
      const domImg = document.querySelector('.product-gallery__media:not([hidden]) img, [data-media-id]:not([hidden]) img');
      if (domImg && domImg.src) return domImg.src;

      // 5. Product featured image
      if (d && d.featured_image) return d.featured_image;

      return null;
    }

    /**
     * Resize a Shopify CDN URL: inserts _{width}x before the file extension.
     * //cdn.shopify.com/.../file.jpg?v=123  ->  //cdn.shopify.com/.../file_240x.jpg?v=123
     */
    _resizeCdnUrl(src, width) {
      if (!src || typeof src !== 'string') return src;
      return src.replace(/(\.\w{2,5})(\?|$)/, '_' + width + 'x$1$2');
    }

    /**
     * Push ONLY the color option to native Prestige variant picker.
     * Prestige resolves the new combined variant and fires variant:change.
     */
    _pushColorToNativePicker(colorTitle) {
      const d = this.mainProductData;
      const picker = document.querySelector('variant-picker');

      if (picker && d && typeof d.color_option_index === 'number' && d.color_option_index >= 0) {
        const colorPos = String(d.color_option_index + 1); // 1-based data-option-position
        const colorInputs = Array.from(
          picker.querySelectorAll('input[data-option-position="' + colorPos + '"]')
        );

        const target = colorInputs.find(inp => {
          const lbl =
            document.querySelector('label[for="' + inp.id + '"]') ||
            picker.querySelector('label[for="' + inp.id + '"]');
          const lblText = lbl ? lbl.textContent.trim() : '';
          const inpVal  = String(inp.value || '').trim();
          const clean   = String(colorTitle).trim();
          return lblText === clean || inpVal === clean || lblText.toLowerCase() === clean.toLowerCase();
        });

        if (target) {
          if (!target.checked) {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }

      // Fallback for dropdown-style color picker
      if (picker) {
        const selects = Array.from(picker.querySelectorAll('select'));
        for (const sel of selects) {
          const matchOpt = Array.from(sel.options).find(
            o => o.text.trim() === String(colorTitle).trim() || o.value === String(colorTitle)
          );
          if (matchOpt) {
            sel.value = matchOpt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }

    /**
     * Push ONLY the size option to native Prestige variant picker.
     * Color is left unchanged; Prestige resolves the new combined variant.
     */
    _pushSizeToNativePicker(sizeTitle) {
      const d = this.mainProductData;
      const picker = document.querySelector('variant-picker');

      if (picker && d && typeof d.size_option_index === 'number' && d.size_option_index >= 0) {
        const sizePos = String(d.size_option_index + 1); // 1-based data-option-position
        const sizeInputs = Array.from(
          picker.querySelectorAll('input[data-option-position="' + sizePos + '"]')
        );

        const target = sizeInputs.find(inp => {
          const lbl =
            document.querySelector('label[for="' + inp.id + '"]') ||
            picker.querySelector('label[for="' + inp.id + '"]');
          const lblText = lbl ? lbl.textContent.trim() : '';
          const inpVal  = String(inp.value || '').trim();
          const clean   = String(sizeTitle).trim();
          return lblText === clean || inpVal === clean || lblText.toLowerCase() === clean.toLowerCase();
        });

        if (target) {
          if (!target.checked) {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }

      // Fallback: dropdown-style picker
      if (picker) {
        const selects = Array.from(picker.querySelectorAll('select'));
        for (const sel of selects) {
          const matchOpt = Array.from(sel.options).find(
            o => o.text.trim() === String(sizeTitle).trim() || o.value === String(sizeTitle)
          );
          if (matchOpt) {
            sel.value = matchOpt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }

      // Last resort: find variant for current color + new size, update form input
      const colorValEl = this.querySelector('.slot-main-color-val');
      const colorVal   = colorValEl ? colorValEl.textContent.trim() : '';

      if (d && Array.isArray(d.variants)) {
        const match =
          d.variants.find(v => v.color === colorVal && String(v.size) === String(sizeTitle) && v.available) ||
          d.variants.find(v => v.color === colorVal && String(v.size) === String(sizeTitle));

        if (match) {
          const form = document.querySelector('form[action*="/cart/add"]');
          if (form) {
            const idInput = form.querySelector('input[name="id"], select[name="id"]');
            if (idInput) {
              idInput.value = match.id;
              idInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }
    }

    _esc(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    /* ------------------------------------------------------------------------
       SLOT ACTIONS (MÓDOSÍTÁS BUTTON)
       ------------------------------------------------------------------------ */
    initSlotActions() {
      this.addEventListener('click', (e) => {
        const changeBtn = e.target.closest('.slot-extra-change-btn');
        if (changeBtn) {
          e.preventDefault();
          e.stopPropagation();
          const slot = changeBtn.closest('.product-bundle-slot--extra');
          if (slot) this.openPickerModal(slot);
        }
      });
    }

    /* ------------------------------------------------------------------------
       CHOOSE PRODUCT MODAL
       ------------------------------------------------------------------------ */
    /* ------------------------------------------------------------------------
       CHOOSE PRODUCT MODAL & EXTRA SLOTS MULTI-COLOR MANAGEMENT
       ------------------------------------------------------------------------ */
    initChooseModal() {
      const backdrop = this.querySelector('.product-picker-modal-backdrop');
      if (!backdrop) return;

      const closeBtn = backdrop.querySelector('.product-picker-modal__close-btn');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closePickerModal());

      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.closePickerModal();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !backdrop.hidden) this.closePickerModal();
      });

      // Open triggers
      this.addEventListener('click', (e) => {
        const trigger =
          e.target.closest('.slot-open-modal-btn') ||
          e.target.closest('.product-bundle-slot__trigger-btn') ||
          e.target.closest('.product-bundle-slot__image-placeholder');
        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          const slot = trigger.closest('.product-bundle-slot--extra');
          this.openPickerModal(slot);
        }
      });

      // Modal item interactions
      const listItems = backdrop.querySelectorAll('.product-picker-list-item');
      listItems.forEach(item => {
        const productId   = item.dataset.productId;
        const chooseBtn   = item.querySelector('.picker-item-choose-btn');
        const sizeSelect  = item.querySelector('.picker-item-size-select');
        const colorSelect = item.querySelector('.picker-item-color-select');
        const imgEl       = item.querySelector('.product-picker-list-item__img-box img');

        // Look up product data from eligibleProducts
        const pData = Array.isArray(this.eligibleProducts)
          ? this.eligibleProducts.find(p => String(p.id) === String(productId))
          : null;

        if (colorSelect && pData && Array.isArray(pData.variants)) {
          colorSelect.addEventListener('change', (e) => {
            const selectedColor = e.target.value;
            const colorVars = pData.variants.filter(v => v.color === selectedColor);

            if (colorVars.length && sizeSelect) {
              sizeSelect.innerHTML = colorVars.map(v => {
                const dis = !v.available ? 'disabled="disabled"' : '';
                const txt = !v.available ? ' - Készlethiány' : '';
                return '<option value="' + v.id + '" data-size-title="' + this._esc(v.size) + '" data-color-title="' + this._esc(v.color) + '" ' + dis + '>' + this._esc(v.size) + txt + '</option>';
              }).join('');

              const firstAvail = colorVars.find(v => v.available) || colorVars[0];
              if (firstAvail) sizeSelect.value = firstAvail.id;
            }

            let newImgSrc = (pData.color_images && pData.color_images[selectedColor])
              || (colorVars[0] && colorVars[0].featured_image)
              || pData.featured_image;
            if (imgEl && newImgSrc) imgEl.src = newImgSrc;
          });
        }

        if (chooseBtn && sizeSelect) {
          chooseBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const variantId        = String(sizeSelect.value || '');
            const title            = item.querySelector('.product-picker-list-item__title')?.textContent.trim() || '';
            const imgSrc           = imgEl ? imgEl.src : '';
            const colorVal         = colorSelect ? colorSelect.value : '';
            const sizeOptionsHtml  = sizeSelect.innerHTML;
            const colorOptionsHtml = colorSelect ? colorSelect.innerHTML : '';

            let targetSlot = this.activeSlotElement;
            if (!targetSlot || !this.selectedCard || !this.selectedCard.contains(targetSlot)) {
              targetSlot = this.selectedCard
                ? (
                  this.selectedCard.querySelector('.product-bundle-slot--extra[data-selected-variant-id=""]') ||
                  this.selectedCard.querySelector('.product-bundle-slot--extra:not([data-selected-variant-id])') ||
                  this.selectedCard.querySelector('.product-bundle-slot--extra')
                )
                : null;
            }

            if (targetSlot) {
              this.applyProductDataToSlot(targetSlot, {
                productId,
                variantId,
                title,
                imgSrc,
                sizeOptionsHtml,
                colorOptionsHtml,
                colorVal,
                pData
              });
              this.closePickerModal();
              this.hideError();
              this.updateCtaPrice();
            }
          });
        }
      });
    }

    openPickerModal(slot) {
      if (!slot && this.selectedCard) {
        slot =
          this.selectedCard.querySelector('.product-bundle-slot--extra[data-selected-variant-id=""]') ||
          this.selectedCard.querySelector('.product-bundle-slot--extra:not([data-selected-variant-id])') ||
          this.selectedCard.querySelector('.product-bundle-slot--extra');
      }
      this.activeSlotElement = slot;

      const backdrop = this.querySelector('.product-picker-modal-backdrop');
      if (backdrop) {
        backdrop.hidden = false;
        backdrop.style.display = 'flex';
        document.body.style.overflow = 'hidden';
      }
    }

    closePickerModal() {
      const backdrop = this.querySelector('.product-picker-modal-backdrop');
      if (backdrop) {
        backdrop.hidden = true;
        backdrop.style.display = 'none';
      }
      document.body.style.overflow = '';
    }

    applyProductDataToSlot(slot, { productId, variantId, title, imgSrc, sizeOptionsHtml, colorOptionsHtml, colorVal, pData }) {
      if (!slot) return;

      slot.setAttribute('data-selected-variant-id', variantId);
      slot.dataset.selectedVariantId = String(variantId);
      if (productId) slot.setAttribute('data-selected-product-id', productId);

      const emptyState  = slot.querySelector('.product-bundle-slot__empty-state');
      const filledState = slot.querySelector('.product-bundle-slot__filled-state');
      const slotImg     = slot.querySelector('.slot-extra-img');
      const slotTitle   = slot.querySelector('.slot-extra-title');
      const sizeSelect  = slot.querySelector('.slot-extra-size-select');
      const colorWrap   = slot.querySelector('.product-bundle-slot__color-wrap');
      const colorSelect = slot.querySelector('.slot-extra-color-select');

      if (emptyState)  { emptyState.hidden  = true;  emptyState.style.display  = 'none'; }
      if (filledState) { filledState.hidden = false; filledState.style.display = 'flex'; }

      if (slotImg && imgSrc) { slotImg.src = imgSrc; slotImg.alt = title; }
      if (slotTitle)          { slotTitle.textContent = title; }

      // Handle Color Dropdown in Slot
      if (colorSelect && colorOptionsHtml) {
        colorSelect.innerHTML = colorOptionsHtml;
        colorSelect.value = colorVal;
        if (colorWrap) {
          colorWrap.hidden = false;
          colorWrap.style.display = 'inline-flex';
        }

        colorSelect.onchange = (e) => {
          const newColor = e.target.value;
          const prodData = pData || (Array.isArray(this.eligibleProducts) ? this.eligibleProducts.find(p => String(p.id) === String(productId)) : null);

          if (prodData && Array.isArray(prodData.variants)) {
            const colorVars = prodData.variants.filter(v => v.color === newColor);
            if (colorVars.length && sizeSelect) {
              const currentOpt = sizeSelect.options[sizeSelect.selectedIndex];
              const currentSize = currentOpt ? (currentOpt.dataset.sizeTitle || currentOpt.text.replace(' - Készlethiány', '')).trim() : '';

              sizeSelect.innerHTML = colorVars.map(v => {
                const sel = String(v.size) === String(currentSize) ? 'selected="selected"' : '';
                const dis = !v.available ? 'disabled="disabled"' : '';
                const txt = !v.available ? ' - Készlethiány' : '';
                return '<option value="' + v.id + '" data-size-title="' + this._esc(v.size) + '" data-color-title="' + this._esc(v.color) + '" ' + sel + ' ' + dis + '>' + this._esc(v.size) + txt + '</option>';
              }).join('');

              const matchVar = colorVars.find(v => String(v.size) === String(currentSize) && v.available)
                || colorVars.find(v => v.available)
                || colorVars[0];

              if (matchVar) {
                sizeSelect.value = matchVar.id;
                slot.setAttribute('data-selected-variant-id', matchVar.id);
                slot.dataset.selectedVariantId = String(matchVar.id);
              }
            }

            let newImg = (prodData.color_images && prodData.color_images[newColor])
              || (colorVars[0] && colorVars[0].featured_image)
              || prodData.featured_image;
            if (slotImg && newImg) slotImg.src = newImg;
          }
        };
      } else if (colorWrap) {
        colorWrap.hidden = true;
        colorWrap.style.display = 'none';
      }

      // Handle Size Dropdown in Slot
      if (sizeSelect && sizeOptionsHtml) {
        sizeSelect.innerHTML = sizeOptionsHtml;
        sizeSelect.value = variantId;
        sizeSelect.onchange = (e) => {
          slot.setAttribute('data-selected-variant-id', e.target.value);
          slot.dataset.selectedVariantId = e.target.value;
        };
      }
    }

    /* ------------------------------------------------------------------------
       PRICE + ERROR
       ------------------------------------------------------------------------ */
    updateCtaPrice() {
      if (!this.selectedCard) return;
      const priceStr     = this.selectedCard.dataset.offerPriceFormatted || '';
      const ctaPriceSpan = this.querySelector('.product-bundle-offer__cta-price');
      if (ctaPriceSpan && priceStr) ctaPriceSpan.textContent = ' \u2022 ' + priceStr;
    }

    showError(msg) {
      const errBox = this.querySelector('.product-bundle-offer__error');
      if (errBox) { errBox.textContent = msg; errBox.hidden = false; }
    }

    hideError() {
      const errBox = this.querySelector('.product-bundle-offer__error');
      if (errBox) errBox.hidden = true;
    }

    /* ------------------------------------------------------------------------
       ADD TO CART
       ------------------------------------------------------------------------ */
    initAddToCart() {
      const ctaButton = this.querySelector('.product-bundle-offer__cta-button');
      if (!ctaButton) return;
      ctaButton.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.handleAddToCart(ctaButton);
      });
    }

    async handleAddToCart(button) {
      if (!this.selectedCard) return;

      const offerQuantity = parseInt(this.selectedCard.dataset.offerQuantity || '1', 10);
      const itemsToAdd    = [];
      let unselectedCount = 0;

      // SLOT #1: current product
      const mainVariantId =
        this.getAttribute('data-current-variant-id') ||
        document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;

      if (!mainVariantId) {
        this.showError('Kérjük, válaszd ki a fő termék méretét!');
        return;
      }

      itemsToAdd.push({
        id: parseInt(mainVariantId, 10),
        quantity: 1,
        properties: {
          '_bundle_offer': this.selectedCard.dataset.offerTitle || ('Offer ' + this.selectedOfferIndex),
          '_bundle_target_price': this.selectedCard.dataset.offerPriceRaw || ''
        }
      });

      // Extra slots
      if (offerQuantity > 1) {
        const extraSlots = Array.from(
          this.selectedCard.querySelectorAll('.product-bundle-slot--extra')
        ).slice(0, offerQuantity - 1);

        extraSlots.forEach(slot => {
          const vId = slot.dataset.selectedVariantId || slot.getAttribute('data-selected-variant-id');
          if (!vId) {
            unselectedCount++;
          } else {
            itemsToAdd.push({
              id: parseInt(vId, 10),
              quantity: 1,
              properties: {
                '_bundle_offer': this.selectedCard.dataset.offerTitle || ('Offer ' + this.selectedOfferIndex),
                '_bundle_target_price': this.selectedCard.dataset.offerPriceRaw || ''
              }
            });
          }
        });
      }

      if (unselectedCount > 0) {
        this.showError('Kérjük, válaszd ki mind a ' + offerQuantity + ' terméket a kosárba tétel előtt!');
        return;
      }

      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true })
      );

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const payload = {
          items: itemsToAdd,
          sections: sectionsToBundle.join(',')
        };

        const response = await fetch(Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });

        const responseJson = await response.json();

        if (response.ok) {
          if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === 'page') {
            window.location.href = Shopify.routes.root + 'cart';
            return;
          }

          const cartContent = await (await fetch(Shopify.routes.root + 'cart.js')).json();
          if (responseJson.sections) cartContent['sections'] = responseJson['sections'];

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', cart: cartContent }
            })
          );
        } else {
          this.showError(
            responseJson.description || responseJson.message || 'Hiba történt a kosárba tétel során.'
          );
        }
      } catch (err) {
        console.error('Bundle cart error:', err);
        this.showError('Kommunikációs hiba történt. Kérjük próbáld újra!');
      } finally {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
      }
    }
  }

  window.customElements.define('product-bundle-offer', ProductBundleOffer);
}
